import { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import BrokenImageRoundedIcon from '@mui/icons-material/BrokenImageRounded';
import { alpha, useTheme } from '@mui/material/styles';
import type {
  BiometricMatchComparison,
  BiometricMatchImageSide,
  IdentityLookupUploadedImage,
} from '../../api/identityVerificationDashboardApi';
import { identityVerificationDashboardApi } from '../../api/identityVerificationDashboardApi';
import { OBJECT_STORAGE_KIND, isObjectStorageKind } from '../../lib/storageKind';

function previewStillValid(expiresAt: string | null | undefined): boolean {
  if (!expiresAt) return true;
  const t = Date.parse(expiresAt);
  if (!Number.isFinite(t)) return true;
  // Refresh 30s before expiry so mid-session zoom still works.
  return t - Date.now() > 30_000;
}

/**
 * Prefer short-lived Azure SAS (direct browser→blob). Fall back to access-url
 * mint, then legacy content proxy for filesystem / SAS failures.
 */
async function resolveMatchSideUrl(side: BiometricMatchImageSide): Promise<{
  url: string;
  contentType: string;
  revokeOnCleanup: boolean;
}> {
  if (side.preview_url && previewStillValid(side.preview_url_expires_at)) {
    return { url: side.preview_url, contentType: 'image/jpeg', revokeOnCleanup: false };
  }

  if (isObjectStorageKind(side.storage_kind)) {
    try {
      const access = side.uploaded_image_id
        ? await identityVerificationDashboardApi.uploadedImageAccessUrl(side.uploaded_image_id)
        : await identityVerificationDashboardApi.storageAccessUrl(side.file_path);
      return { url: access.url, contentType: access.contentType || 'image/jpeg', revokeOnCleanup: false };
    } catch {
      // fall through to byte proxy
    }
  }

  const blob = side.uploaded_image_id
    ? await identityVerificationDashboardApi.uploadedImageContent(side.uploaded_image_id)
    : await identityVerificationDashboardApi.storageContentByPath(side.file_path);
  return {
    url: URL.createObjectURL(blob),
    contentType: (blob.type || 'image/jpeg').toLowerCase(),
    revokeOnCleanup: true,
  };
}

function MatchSidePreview({
  side,
  size = 168,
  onClick,
}: {
  side: BiometricMatchImageSide;
  size?: number;
  onClick?: (objectUrl: string, contentType: string) => void;
}) {
  const theme = useTheme();
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'ready'; url: string; contentType: string; revokeOnCleanup: boolean }
    | { kind: 'error'; reason: string }
  >({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    let revokeUrl: string | null = null;
    setState({ kind: 'loading' });
    resolveMatchSideUrl(side)
      .then((resolved) => {
        if (cancelled) {
          if (resolved.revokeOnCleanup) URL.revokeObjectURL(resolved.url);
          return;
        }
        if (resolved.revokeOnCleanup) revokeUrl = resolved.url;
        setState({
          kind: 'ready',
          url: resolved.url,
          contentType: resolved.contentType,
          revokeOnCleanup: resolved.revokeOnCleanup,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          kind: 'error',
          reason: err instanceof Error ? err.message : 'Could not load image.',
        });
      });
    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [side.uploaded_image_id, side.file_path, side.preview_url, side.preview_url_expires_at, side.storage_kind]);

  const containerSx = {
    width: size,
    height: size,
    borderRadius: 1.5,
    border: 1,
    borderColor: 'divider',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
    bgcolor: alpha(theme.palette.text.primary, 0.04),
    cursor: state.kind === 'ready' && onClick ? 'zoom-in' : 'default',
  };

  if (state.kind === 'loading') {
    return (
      <Box sx={containerSx}>
        <CircularProgress size={22} />
      </Box>
    );
  }
  if (state.kind === 'error') {
    return (
      <Tooltip title={state.reason} arrow>
        <Box sx={containerSx}>
          <Stack alignItems="center" spacing={0.25} px={1}>
            <BrokenImageRoundedIcon fontSize="small" color="error" />
            <Typography variant="caption" color="error" textAlign="center">
              Unavailable
            </Typography>
          </Stack>
        </Box>
      </Tooltip>
    );
  }
  return (
    <Box sx={containerSx} onClick={() => onClick?.(state.url, state.contentType)}>
      <Box
        component="img"
        src={state.url}
        alt={side.label}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </Box>
  );
}

function sideAsUploadImage(side: BiometricMatchImageSide): IdentityLookupUploadedImage {
  return {
    id: side.uploaded_image_id ?? side.file_path,
    image_type: 'selfie',
    sequence_index: 0,
    file_path: side.file_path,
    original_filename: null,
    content_type: 'image/jpeg',
    size_bytes: 0,
    sha256_hash: '',
    storage_kind: side.storage_kind ?? OBJECT_STORAGE_KIND,
    created_at: '',
    iv_verification_id: side.iv_verification_id,
    preview_url: side.preview_url ?? null,
    preview_url_expires_at: side.preview_url_expires_at ?? null,
  };
}

export function BiometricMatchComparisonPanel({
  comparison,
  onZoom,
}: {
  comparison: BiometricMatchComparison;
  onZoom: (v: {
    image: IdentityLookupUploadedImage;
    url: string;
    contentType: string;
  }) => void;
}) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
        {comparison.passed === true ? (
          <Chip size="small" color="success" variant="outlined" label="Matched" sx={{ height: 22, fontWeight: 700 }} />
        ) : comparison.passed === false ? (
          <Chip size="small" color="error" variant="outlined" label="Did not match" sx={{ height: 22, fontWeight: 700 }} />
        ) : (
          <Chip size="small" variant="outlined" label="Compared" sx={{ height: 22 }} />
        )}
        <Typography variant="caption" color="text.secondary">
          {comparison.kind === 'RETURNING_SELFIE_1TO1'
            ? 'This attempt vs on-file selfie'
            : 'This attempt vs matched customer selfie'}
        </Typography>
      </Stack>

      {!comparison.probe && !comparison.reference ? (
        <Typography variant="body2" color="text.secondary">
          Selfie images for this comparison are not available yet.
        </Typography>
      ) : (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'flex-start' }}
          justifyContent="flex-start"
        >
          {[comparison.probe, comparison.reference].map((side, idx) => (
            <Stack key={idx} spacing={0.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" textAlign="center">
                {side?.label ?? (idx === 0 ? 'This application' : 'On-file customer')}
              </Typography>
              {side ? (
                <MatchSidePreview
                  side={side}
                  size={140}
                  onClick={(url, contentType) =>
                    onZoom({ image: sideAsUploadImage(side), url, contentType })
                  }
                />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Image not available
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}
